'use client'

import { cn } from '@/lib/utils'

import { LoadingDots, Separator } from '@/components/ui'

interface RibbonCardProps extends React.ComponentPropsWithoutRef<'div'> {
  list: RibbonLineProps[]
  highlightList?: RibbonLineProps[]
  ribbonClassName?: string
  listClassName?: string
  highlightsClassName?: string
  isLoading?: boolean
}

export const RibbonCard = ({
  className,
  list,
  highlightList,
  ribbonClassName,
  listClassName,
  highlightsClassName,
  isLoading,
  ...restProps
}: RibbonCardProps) => {
  return (
    <div
      className={cn(
        'flex w-full flex-col rounded-lg bg-gray-100 pb-5 pt-4',
        className,
      )}
      {...restProps}
    >
      <div
        className={cn(
          'flex w-full flex-col gap-0.5 px-5 pb-3 text-sm font-medium',
          listClassName,
        )}
      >
        {list?.map((line, index) => (
          <RibbonLine key={index} {...line} isLoading={isLoading} />
        ))}
      </div>

      {highlightList && highlightList?.length > 0 && (
        <div
          className={cn(
            'relative flex w-full flex-col gap-3 px-5',
            "before:contents-[''] before:absolute before:-left-2.5 before:-top-2.5 before:size-5 before:rounded-full before:bg-white",
            "after:contents-[''] after:absolute after:-right-2.5 after:-top-2.5 after:size-5 after:rounded-full after:bg-white",
            ribbonClassName,
          )}
        >
          <Separator className="w-full border-t border-dashed border-gray-300/60 bg-transparent" />

          <div
            className={cn(
              'flex w-full flex-col gap-1 text-sm font-medium',
              highlightsClassName,
            )}
          >
            {highlightList?.map(
              (
                { label, value, className, labelClassName, valueClassName },
                index,
              ) => (
                <RibbonLine
                  key={index}
                  label={label}
                  value={value}
                  className={cn('font-medium', className)}
                  labelClassName={cn(labelClassName)}
                  valueClassName={cn(valueClassName)}
                  isLoading={isLoading}
                />
              ),
            )}
          </div>
        </div>
      )}
    </div>
  )
}

interface RibbonLineProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string
  value: number | string
  labelClassName?: string
  valueClassName?: string
  isLoading?: boolean
}

const RibbonLine = ({
  label,
  value,
  className,
  labelClassName,
  valueClassName,
  isLoading,
}: RibbonLineProps) => {
  return (
    <div className={cn('flex w-full items-center justify-between', className)}>
      <span className={cn('w-full', labelClassName)}>{label}</span>
      {!isLoading && (
        <span className={cn('font-mono font-medium', valueClassName)}>
          {value}
        </span>
      )}
      {isLoading && (
        <span className="flex justify-end">
          <LoadingDots dotClassName="size-1.5" />
        </span>
      )}
    </div>
  )
}
